<template>
  <notification v-bind="$attrs" :item="item">
    <template #logo>
      <span>
        <BittsAvatar
          v-if="partnerOrg"
          :org="partnerOrg"
          :show-initials="true"
          size="x-small"
        />
        <BittsAvatar
          v-else
          :is-icon="true"
          icon-slot-bg-color="bg-beta-background-medium"
          size="x-small"
        >
          <template #icon>
            <FontAwesomeIcon
              :icon="['fak', 'company']"
              :style="{ height: '20px', width: '20px', color: 'white' }"
            />
          </template>
        </BittsAvatar>
      </span>
    </template>
    <template #message> Partnership Request </template>
    <template #description>
      {{ userName }}
      <span v-if="userLastName">{{ userLastName }}</span>
      <span v-if="userEmail">({{ userEmail }})</span>
      from {{ organizationName }} wants to partner with you.
    </template>
    <template #action>
      <div v-if="request" class="flex items-center gap-4">
        <BittsButton
          text="Accept"
          type="primary"
          size="x-small"
          @click="acceptInvitation(request)"
        />
        <BittsButton
          text="Decline"
          type="danger"
          variant="ghost"
          size="x-small"
          @click="declineInvitation(request)"
        />
      </div>
      <div v-else-if="partnerOrg.id" class="flex items-center">
        <BittsButton
          text="View Partner"
          type="neutral"
          size="x-small"
          @click="viewPartner"
        />
      </div>
    </template>
    <template #icon>
      <FontAwesomeIcon
        :icon="['fak', 'team']"
        :style="{ height: '12px', width: '12px', color: 'currentColor' }"
        class="text-neutral-accent ml-4"
      />
    </template>
  </notification>
  <Teleport to="body">
    <DeclineReasonModal
      v-if="decliningRequest"
      :partner-name="decliningRequest.sending_organization?.name ?? ''"
      :partner="decliningRequest.sending_organization ?? null"
      :loading="isDecliningLoading"
      @confirm="confirmDecline"
      @close="decliningRequest = null"
    />
  </Teleport>
</template>
<script setup lang="ts">
import { BittsAvatar, BittsButton } from '@crossbeam/bitts';

import { computed, ref } from 'vue';
import { useRouter } from 'vue-router';

import DeclineReasonModal from '@/components/dash/DeclineReasonModal.vue';
import Notification from '@/components/notifications/Notification.vue';

import { crossbeamApi } from '@/api';
import { usePartnerTrackerTracking } from '@/composables/usePartnerTrackerTracking';
import { DeclineReasonType } from '@/constants/decline_reasons';
import { TEMP_PARTNER_TRACKER_ACCESS } from '@/constants/feature_flags';
import {
  useFeatureFlagStore,
  useFlashesStore,
  useNotificationsStore,
  usePartnersStore,
} from '@/stores';
import { Notification as NotificationType } from '@/types/notifications';
import { ReceivedPartnershipProposal } from '@/types/proposal';

const { item } = defineProps<{
  item: NotificationType;
}>();

const emit = defineEmits<(e: 'action-clicked', action: string) => void>();

defineOptions({ inheritAttrs: false });

const userName = computed(() => item.data?.inviter_user_name);
const userLastName = computed(() => item.data?.inviter_last_name || '');
const userEmail = computed(() => item.data?.inviter_email || '');
const organizationName = computed(() => item.data?.inviter_org_name);

const partnersStore = usePartnersStore();
const request = computed(() =>
  partnersStore.getProposalReceivedById(
    (item.object_id as { proposal_id: number }).proposal_id,
  ),
);
const partnerOrg = computed(() => {
  const org = partnersStore.getPartnerOrgById(
    item.data?.inviter_org_id as unknown as number,
  );
  return (
    org || {
      clearbit_domain: request.value?.sending_organization.clearbit_domain,
      id: '',
    }
  );
});

const router = useRouter();
const flashesStore = useFlashesStore();
const notificationsStore = useNotificationsStore();
const { hasFeatureFlag } = useFeatureFlagStore();
const tracking = usePartnerTrackerTracking();

const decliningRequest = ref<ReceivedPartnershipProposal | null>(null);
const isDecliningLoading = ref(false);
async function isValidProposal(pid: number) {
  try {
    const { data, error } = await crossbeamApi.GET('/v0.1/proposals-received');
    if (error) throw error;
    const proposals = data?.items ?? [];
    return proposals.some((invite) => invite.id === pid);
  } catch (err) {
    flashesStore.addErrorFlash({ message: err as string });
    return false;
  }
}
async function acceptInvitation(proposal: ReceivedPartnershipProposal) {
  emit('action-clicked', 'Accept');
  try {
    const hasProposal = await isValidProposal(proposal.id);
    if (hasProposal) {
      await router.push({
        name: 'accept_proposal',
        query: { proposal_id: proposal.id },
      });
      await notificationsStore.markRead({ id: item.id, isRead: true });
    } else {
      flashesStore.addErrorFlash({
        message: "Partnership Request Doesn't Exist",
        description:
          'If you want to partner with them, you can send them a request.',
      });
    }
  } catch (err) {
    flashesStore.addErrorFlash({ message: err as string });
  } finally {
    await partnersStore.refreshPartnersStore();
  }
}
async function declineInvitation(proposal: ReceivedPartnershipProposal) {
  const hasProposal = await isValidProposal(proposal.id);
  if (!hasProposal) {
    flashesStore.addErrorFlash({
      message: "Partnership Request Doesn't Exist",
      description:
        'If you want to partner with them, you can send them a request.',
    });
    emit('action-clicked', 'Decline');
    await partnersStore.refreshPartnersStore();
    return;
  }

  if (hasFeatureFlag(TEMP_PARTNER_TRACKER_ACCESS)) {
    emit('action-clicked', 'Decline');
    decliningRequest.value = proposal;
    return;
  }
  await silentDecline(proposal);
}

async function silentDecline(proposal: ReceivedPartnershipProposal) {
  try {
    await partnersStore.declineProposal(proposal.id);
    flashesStore.addSuccessFlash({
      message: `Invitation from ${proposal.sending_organization.name} rejected`,
    });
    emit('action-clicked', 'Decline');
    try {
      await notificationsStore.markRead({ id: item.id, isRead: true });
    } catch {
      // best-effort
    }
  } catch (err) {
    flashesStore.addErrorFlash({ message: err as string });
  } finally {
    await partnersStore.refreshPartnersStore();
  }
}

async function confirmDecline(payload: {
  decline_reason: DeclineReasonType;
  decline_note: string | null;
}) {
  const proposal = decliningRequest.value;
  if (!proposal) return;
  isDecliningLoading.value = true;
  try {
    await partnersStore.declineProposal(proposal.id, payload);
    tracking.trackDeclineSubmitted(
      payload.decline_reason,
      Boolean(payload.decline_note),
    );
    flashesStore.addSuccessFlash({
      message: `Partnership request from ${proposal.sending_organization.name} declined`,
    });
    decliningRequest.value = null;
    try {
      await notificationsStore.markRead({ id: item.id, isRead: true });
    } catch {
      // best-effort
    }
  } catch (err) {
    flashesStore.addErrorFlash({ message: err as string });
  } finally {
    isDecliningLoading.value = false;
    await partnersStore.refreshPartnersStore();
  }
}
async function viewPartner() {
  emit('action-clicked', 'View Partner');
  await notificationsStore.markRead({ id: item.id, isRead: true });
  await router.push({
    name: 'partner_details',
    params: { partner_org_id: partnerOrg.value?.id },
  });
}
</script>
